/**
 * OwnerLink (Sawyer F2) — the owner's name, as a link to their client profile
 * when the signed-in staffer can open it, plain text otherwise.
 *
 * Sawyer, 2026-10-23, tapping an owner's name that did nothing: "it'd be nice
 * if we could click on the owner right here." The name the row already shows
 * becomes the control (no new button, DESIGN-PHILOSOPHY §1), with a visible
 * underline at rest and a 44×44 hit area that does not grow the row (§7).
 *
 * Roles come from `readSession()` (provider-free, never throws — the same
 * read PermissionGate and DogDetail use). No session ⇒ no roles ⇒ text: the
 * component fails closed. It draws ONLY the name; owner flags and any
 * "new note" marker stay siblings in each surface.
 */
import { Link } from 'react-router-dom';
import { readSession } from '../../lib/api.js';
import { effectiveRolesOf } from '../../lib/rbac-matrix.js';
import { canOpenOwner, ownerProfilePath } from './can-open-owner.js';
import './owner-link.css';

export interface OwnerLinkProps {
  /** The owner's display name, exactly as the surface rendered it before. */
  name: string;
  /** The customer id; null/undefined/'' ⇒ plain text (older server, no owner). */
  customerId: string | null | undefined;
  /** Extra class for the surface's own typography (the link inherits colour/size). */
  className?: string;
  /** Called on activation before navigation (the Dog ID card closes itself). */
  onNavigate?: () => void;
  /** Stable test hook; the link defaults to `owner-link-<customerId>`, the text branch carries it only when given. */
  testId?: string;
}

/** The staffer's effective roles from the persisted session ([] when signed out). */
export function sessionRoles(): ReadonlyArray<string> {
  return effectiveRolesOf(readSession().staff);
}

export function OwnerLink({
  name,
  customerId,
  className,
  onNavigate,
  testId,
}: OwnerLinkProps): JSX.Element {
  if (!canOpenOwner(sessionRoles(), customerId)) {
    // Text branch: same text, same surface class; an explicit testId follows
    // it so a test can tell "text" from "missing".
    return (
      <span className={className} data-testid={testId}>
        {name}
      </span>
    );
  }
  return (
    <Link
      to={ownerProfilePath(customerId)}
      className={['ferro-ownerlink', className].filter(Boolean).join(' ')}
      aria-label={`Open ${name}'s client profile`}
      data-testid={testId ?? `owner-link-${customerId}`}
      onClick={onNavigate}
    >
      {name}
    </Link>
  );
}
